import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { IsoDate, IsoMonth, Money, PaymentMethod, PaymentStatus, Role, Timestamp } from '@/lib/api/types';

export type ApprovalType = 'DISCOUNT' | 'ADVANCE_DECISION' | 'ADVANCE_PAYOUT' | 'PAYROLL_APPROVAL' | 'PAYROLL_PAYMENT';

export interface Approval {
  type: ApprovalType;
  id: string;
  title: string;
  detail: string;
  /** Money string, or "10%" for a percent discount, or null. */
  amount: string | null;
}

export interface RecentReceipt {
  id: string;
  receiptNumber: string;
  payerName: string;
  students: string[];
  method: PaymentMethod;
  status: PaymentStatus;
  amount: Money;
  paidAt: Timestamp;
}

/** Sections of switched-off features are absent, not zero. */
export interface Dashboard {
  today: IsoDate;
  currency: string;
  role: Role;
  fees?: {
    collectedToday: { amount: Money; receipts: number };
    pendingClearance: { amount: Money; count: number };
    thisMonth: { month: IsoMonth; due: Money; collected: Money; percent: number | null };
    overdue: { students: number; amount: Money };
    expected: Money;
    collected: Money;
  };
  recentReceipts?: RecentReceipt[];
  advances?: { open: number; outstanding: Money };
  /** Present when student attendance is on. */
  attendance?: {
    date: IsoDate;
    holiday: boolean;
    classes: number;
    classesDone: number;
    students: number;
    marked: number;
    PRESENT: number;
    ABSENT: number;
    LATE: number;
    EXCUSED: number;
  };
  approvals: Approval[];
}

export const dashboardKey = ['dashboard'] as const;

export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: dashboardKey,
    queryFn: () => api.get<Dashboard>('/dashboard'),
    staleTime: 60_000,
    enabled,
  });
}
